/**
 * eegSyncChannel — EEG-CUX-005
 *
 * BroadcastChannel wrapper and message type definitions for time-syncing
 * the main EEGViewer with popped-out dock panels.
 *
 * Channel name is scoped to the recording ID so concurrent viewer sessions
 * for different recordings do not interfere with each other.
 */

// ── Message types ────────────────────────────────────────────────────────────

export type EegSyncMessage =
  | { type: "time-update"; timeSeconds: number; pageEndSeconds: number }
  | { type: "montage-change"; montageId: string }
  | {
      type: "filter-change";
      highPass: number;
      lowPass: number;
      notch60hz: boolean;
      notch50hz: boolean;
    }
  | { type: "gain-change"; sensitivityUvPerMm: number }
  | { type: "pop-in-request"; panelKey: string };

// ── Channel factory ──────────────────────────────────────────────────────────

/**
 * Returns a BroadcastChannel scoped to the given recording ID.
 * Returns `null` if BroadcastChannel is not supported (e.g. old Safari).
 */
export function createEegSyncChannel(
  recordingId: string,
): BroadcastChannel | null {
  if (typeof BroadcastChannel === "undefined") return null;
  return new BroadcastChannel(`eeg-viewer-sync-${recordingId}`);
}

// ── Throttle helper ──────────────────────────────────────────────────────────

/**
 * Returns a throttled function that fires at most once per animation frame.
 * Subsequent calls within a single frame are coalesced — only the latest
 * invocation is dispatched. This is used for `time-update` broadcasts to
 * avoid flooding the BroadcastChannel during waveform drag.
 */
export function throttleToAnimationFrame<T extends (...args: never[]) => void>(
  fn: T,
): (...args: Parameters<T>) => void {
  let rafId: number | null = null;
  let latestArgs: Parameters<T> | null = null;

  return (...args: Parameters<T>): void => {
    latestArgs = args;
    if (rafId !== null) return; // already scheduled
    rafId = requestAnimationFrame(() => {
      rafId = null;
      if (latestArgs !== null) {
        fn(...latestArgs);
        latestArgs = null;
      }
    });
  };
}

// ── Serialisation helpers ────────────────────────────────────────────────────

/** Type-guard for EegSyncMessage, used on the receive side. */
export function isEegSyncMessage(value: unknown): value is EegSyncMessage {
  if (typeof value !== "object" || value === null) return false;
  const msg = value as Record<string, unknown>;
  const validTypes = new Set([
    "time-update",
    "montage-change",
    "filter-change",
    "gain-change",
    "pop-in-request",
  ]);
  return typeof msg.type === "string" && validTypes.has(msg.type);
}
